/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Pagination,
  Select,
  Snackbar,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { createPortal } from 'react-dom'
import { Helmet } from 'react-helmet-async'
import { useSearchParams } from 'react-router-dom'

import {
  DAI_ZERO_REVIEW_CROPS,
  DAI_ZERO_REVIEW_MODELS,
  RELABEL_DAI_ZERO_CROPS,
  SET_DAI_ZERO_CROP_EXCLUSION,
  UNDO_HISTORICAL_DAI_ZERO_RELABELS,
  UNVERIFY_DAI_ZERO_CROPS,
  type UltraDAIZeroReviewCropsQuery,
  type UltraDAIZeroReviewCropsQueryVariables,
  type UltraDAIZeroReviewInput,
  UltraDAIZeroCropSource,
  UltraDAIZeroReviewSort,
  UltraDAIZeroReviewStatus,
  UltraDAIZeroReviewMembership,
  UltraDAIZeroVerificationSource,
  type UltraDAIZeroReviewModelsQuery,
  type UltraDAIZeroReviewModelsQueryVariables,
  type UltraRelabelDAIZeroCropsMutation,
  type UltraRelabelDAIZeroCropsMutationVariables,
  type UltraSetDAIZeroCropExclusionMutation,
  type UltraSetDAIZeroCropExclusionMutationVariables,
  type UltraUndoHistoricalDAIZeroRelabelsMutation,
  type UltraUndoHistoricalDAIZeroRelabelsMutationVariables,
  type UltraUnverifyDAIZeroCropsMutation,
  type UltraUnverifyDAIZeroCropsMutationVariables,
} from '@dai/graphql'
import useT from '@/i18n/useT'
import DAIZeroReviewCropCard from './DAIZeroReviewCropCard'
import {
  buildDAIZeroReviewCropInput,
  getDAIZeroReviewEligibility,
} from './daiZeroReviewUtils'
import FullImageDialog from './FullImageDialog'
import type { SearchProduct } from './labelingHelpers'
import ProductImageBox from './ProductImageBox'
import ProductModelExclusionControl from './ProductModelExclusionControl'
import ProductSearchAutocomplete from './ProductSearchAutocomplete'

const PAGE_SIZE = 200
const ALL_STATUSES = Object.values(UltraDAIZeroReviewStatus)
const ALL_VERIFICATION_SOURCES = Object.values(UltraDAIZeroVerificationSource)
const isBlurSort = (value: UltraDAIZeroReviewSort) =>
  value === UltraDAIZeroReviewSort.FOCUS_ASC ||
  value === UltraDAIZeroReviewSort.FOCUS_DESC

const oneYearAgo = () => {
  const date = new Date()
  date.setFullYear(date.getFullYear() - 1)
  return date.toISOString().slice(0, 10)
}

const validEnumValue = <T extends string>(
  values: readonly T[],
  value: string | null,
  fallback: T,
) => (value && values.includes(value as T) ? (value as T) : fallback)

const pageOffsetCursor = (pageNumber: number): string | null => {
  if (pageNumber <= 1) return null
  return String((pageNumber - 1) * PAGE_SIZE)
}

const DAIZeroDatasetReview = () => {
  const { t } = useT()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchProduct, setSearchProduct] =
    React.useState<SearchProduct | null>(null)
  const [searchUpc, setSearchUpc] = React.useState(
    () => searchParams.get('upc') ?? '',
  )
  const [modelVersion, setModelVersion] = React.useState(
    () => searchParams.get('model') ?? '',
  )
  const [draftModelVersion, setDraftModelVersion] = React.useState(
    () => searchParams.get('model') ?? '',
  )
  const [page, setPage] = React.useState(() =>
    Math.max(1, Number(searchParams.get('page')) || 1),
  )
  const [sort, setSort] = React.useState(() =>
    validEnumValue(
      Object.values(UltraDAIZeroReviewSort),
      searchParams.get('sort'),
      UltraDAIZeroReviewSort.CREATED_DESC,
    ),
  )
  const [source, setSource] = React.useState<UltraDAIZeroCropSource | ''>(() =>
    validEnumValue(
      [...Object.values(UltraDAIZeroCropSource), ''],
      searchParams.get('source'),
      '',
    ),
  )
  const [statuses, setStatuses] = React.useState<UltraDAIZeroReviewStatus[]>(
    () => {
      const requested = searchParams.getAll('status')
      return requested.length
        ? requested.filter((status): status is UltraDAIZeroReviewStatus =>
            ALL_STATUSES.includes(status as UltraDAIZeroReviewStatus),
          )
        : [...ALL_STATUSES]
    },
  )
  const [verificationSources, setVerificationSources] = React.useState<
    UltraDAIZeroVerificationSource[]
  >(() => {
    const requested = searchParams.getAll('verificationSource')
    return requested.length
      ? requested.filter((value): value is UltraDAIZeroVerificationSource =>
          ALL_VERIFICATION_SOURCES.includes(
            value as UltraDAIZeroVerificationSource,
          ),
        )
      : [...ALL_VERIFICATION_SOURCES]
  })
  const [membership, setMembership] = React.useState(() =>
    validEnumValue(
      Object.values(UltraDAIZeroReviewMembership),
      searchParams.get('membership'),
      UltraDAIZeroReviewMembership.ALL_ELIGIBLE,
    ),
  )
  const [createdAfter, setCreatedAfter] = React.useState(
    () => searchParams.get('createdAfter') ?? oneYearAgo(),
  )
  const [createdBefore, setCreatedBefore] = React.useState(
    () => searchParams.get('createdBefore') ?? '',
  )
  const [draftSort, setDraftSort] = React.useState(sort)
  const [draftSource, setDraftSource] = React.useState(source)
  const [draftStatuses, setDraftStatuses] = React.useState(statuses)
  const [draftVerificationSources, setDraftVerificationSources] =
    React.useState(verificationSources)
  const [draftMembership, setDraftMembership] = React.useState(membership)
  const [draftCreatedAfter, setDraftCreatedAfter] = React.useState(createdAfter)
  const [draftCreatedBefore, setDraftCreatedBefore] =
    React.useState(createdBefore)
  const lastSelectedIndex = React.useRef<number | null>(null)
  const [selected, setSelected] = React.useState<Set<string>>(new Set())
  const [allMatchingSelected, setAllMatchingSelected] = React.useState(false)
  const [deselectedMatching, setDeselectedMatching] = React.useState<
    Set<string>
  >(new Set())
  const [tileSize, setTileSize] = React.useState(() => {
    const requested = Number(searchParams.get('tileSize'))
    return requested >= 120 && requested <= 300 ? requested : 180
  })
  const [replacementProduct, setReplacementProduct] =
    React.useState<SearchProduct | null>(null)
  const [relabelOpen, setRelabelOpen] = React.useState(false)
  const [confirmExclusion, setConfirmExclusion] = React.useState<
    boolean | null
  >(null)
  const [message, setMessage] = React.useState<string | null>(null)
  const [actionProgress, setActionProgress] = React.useState<string | null>(
    null,
  )
  const [actionError, setActionError] = React.useState<string | null>(null)
  const [fullImageCropId, setFullImageCropId] = React.useState<string | null>(
    null,
  )
  const cropGridRef = React.useRef<HTMLDivElement | null>(null)
  const [filterSidebarElement, setFilterSidebarElement] =
    React.useState<HTMLDivElement | null>(null)
  const [load, { data, loading, error, refetch }] = useLazyQuery<
    UltraDAIZeroReviewCropsQuery,
    UltraDAIZeroReviewCropsQueryVariables
  >(DAI_ZERO_REVIEW_CROPS, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })
  const {
    data: modelsData,
    loading: modelsLoading,
    error: modelsError,
  } = useQuery<
    UltraDAIZeroReviewModelsQuery,
    UltraDAIZeroReviewModelsQueryVariables
  >(DAI_ZERO_REVIEW_MODELS, {
    context: { endPoint: 'mlUltra' },
    fetchPolicy: 'network-only',
  })
  const [relabel, { loading: relabeling }] = useMutation<
    UltraRelabelDAIZeroCropsMutation,
    UltraRelabelDAIZeroCropsMutationVariables
  >(RELABEL_DAI_ZERO_CROPS, { context: { endPoint: 'mlUltra' } })
  const [setExclusion, { loading: excluding }] = useMutation<
    UltraSetDAIZeroCropExclusionMutation,
    UltraSetDAIZeroCropExclusionMutationVariables
  >(SET_DAI_ZERO_CROP_EXCLUSION, { context: { endPoint: 'mlUltra' } })
  const [undoHistoricalRelabel, { loading: undoingRelabel }] = useMutation<
    UltraUndoHistoricalDAIZeroRelabelsMutation,
    UltraUndoHistoricalDAIZeroRelabelsMutationVariables
  >(UNDO_HISTORICAL_DAI_ZERO_RELABELS, {
    context: { endPoint: 'mlUltra' },
  })
  const [unverify, { loading: unverifying }] = useMutation<
    UltraUnverifyDAIZeroCropsMutation,
    UltraUnverifyDAIZeroCropsMutationVariables
  >(UNVERIFY_DAI_ZERO_CROPS, {
    context: { endPoint: 'mlUltra' },
  })
  const reviewModels = modelsData?.daiZeroReviewModels ?? []
  const selectedModel = reviewModels.find(
    model => model.version === modelVersion,
  )
  const connection =
    searchUpc && data?.daiZeroReviewCrops.modelVersion === modelVersion
      ? data.daiZeroReviewCrops
      : undefined
  const crops = connection?.crops ?? []
  const fullImageCrop =
    crops.find(crop => `${crop.source}:${crop.cropId}` === fullImageCropId) ??
    null
  const selectedCrops = crops.filter(crop =>
    allMatchingSelected
      ? !deselectedMatching.has(`${crop.source}:${crop.cropId}`)
      : selected.has(`${crop.source}:${crop.cropId}`),
  )
  const cropInput = selectedCrops.map(buildDAIZeroReviewCropInput)
  const explicitEligibility = getDAIZeroReviewEligibility(selectedCrops)
  const singleStatus = statuses.length === 1 ? statuses[0] : null
  const canRelabel = allMatchingSelected
    ? singleStatus === UltraDAIZeroReviewStatus.UNCHANGED
    : explicitEligibility.canRelabel
  const canExclude = allMatchingSelected
    ? singleStatus === UltraDAIZeroReviewStatus.UNCHANGED
    : explicitEligibility.canExclude
  const canUnverify = allMatchingSelected
    ? singleStatus === UltraDAIZeroReviewStatus.UNCHANGED &&
      source === UltraDAIZeroCropSource.MLULTRA
    : explicitEligibility.canUnverify
  const canCancelExclusion = allMatchingSelected
    ? singleStatus === UltraDAIZeroReviewStatus.EXCLUDED
    : explicitEligibility.canCancelExclusion
  const canUndoHistoricalRelabel = allMatchingSelected
    ? singleStatus === UltraDAIZeroReviewStatus.RELABELED &&
      source === UltraDAIZeroCropSource.HISTORICAL
    : explicitEligibility.canUndoHistoricalRelabel
  const hasAvailableAction =
    canRelabel ||
    canUnverify ||
    canExclude ||
    canCancelExclusion ||
    canUndoHistoricalRelabel
  const selectedCount = allMatchingSelected
    ? Math.max(0, (connection?.totalCount ?? 0) - deselectedMatching.size)
    : selectedCrops.length
  const requiresAsyncProcessing = selectedCount > 500
  const actionLoading = relabeling || excluding || undoingRelabel || unverifying
  const totalPages = connection
    ? Math.max(1, Math.ceil(connection.totalCount / PAGE_SIZE))
    : 1

  React.useEffect(() => {
    if (!modelVersion) {
      const defaultModel = reviewModels.find(model => model.manifestAvailable)
      if (defaultModel) {
        setModelVersion(defaultModel.version)
        setDraftModelVersion(defaultModel.version)
      }
    }
  }, [modelVersion, reviewModels])

  React.useEffect(() => {
    const next = new URLSearchParams()
    if (searchUpc) next.set('upc', searchUpc)
    if (modelVersion) next.set('model', modelVersion)
    if (page > 1) next.set('page', String(page))
    next.set('sort', sort)
    if (source) next.set('source', source)
    statuses.forEach(status => next.append('status', status))
    if (!verificationSources.length) {
      next.append('verificationSource', 'NONE')
    } else if (verificationSources.length !== ALL_VERIFICATION_SOURCES.length) {
      verificationSources.forEach(value =>
        next.append('verificationSource', value),
      )
    }
    next.set('membership', membership)
    if (createdAfter) next.set('createdAfter', createdAfter)
    if (createdBefore) next.set('createdBefore', createdBefore)
    if (tileSize !== 180) next.set('tileSize', String(tileSize))
    setSearchParams(next, { replace: true })
  }, [
    createdAfter,
    createdBefore,
    membership,
    modelVersion,
    page,
    searchUpc,
    setSearchParams,
    sort,
    source,
    statuses,
    tileSize,
    verificationSources,
  ])

  const restoredSearchLoaded = React.useRef(false)
  React.useEffect(() => {
    if (
      !restoredSearchLoaded.current &&
      searchUpc &&
      modelVersion &&
      selectedModel?.manifestAvailable
    ) {
      restoredSearchLoaded.current = true
      void loadCropsPage(page, searchUpc, modelVersion)
    }
  }, [modelVersion, page, searchUpc, selectedModel])

  const reportActionError = (caught: unknown) => {
    setMessage(null)
    setActionError(
      caught instanceof Error
        ? caught.message
        : t('daiZeroReview.error.actionFailed', 'The review action failed.'),
    )
  }

  const loadCropsPage = async (
    targetPage: number,
    upc: string,
    version: string,
    nextSort = sort,
    nextSource = source,
    nextStatuses = statuses,
    nextMembership = membership,
    nextCreatedAfter = createdAfter,
    nextCreatedBefore = createdBefore,
    nextVerificationSources = verificationSources,
    preserveSelection = false,
  ) => {
    lastSelectedIndex.current = null
    if (!preserveSelection) {
      setSelected(new Set())
      setAllMatchingSelected(false)
      setDeselectedMatching(new Set())
    }
    await load({
      variables: {
        input: {
          upc,
          modelVersion: version,
          first: PAGE_SIZE,
          after: pageOffsetCursor(targetPage),
          sort: nextSort,
          source: nextSource || null,
          statuses: nextStatuses,
          verificationSources:
            nextVerificationSources.length === ALL_VERIFICATION_SOURCES.length
              ? null
              : nextVerificationSources,
          membership: nextMembership,
          createdAfter: nextCreatedAfter
            ? new Date(`${nextCreatedAfter}T00:00:00`).toISOString()
            : null,
          createdBefore: nextCreatedBefore
            ? new Date(`${nextCreatedBefore}T23:59:59.999`).toISOString()
            : null,
        },
      },
    })
    setPage(targetPage)
    cropGridRef.current?.scrollTo({ top: 0 })
  }

  const resetSearchState = () => {
    setSelected(new Set())
    setAllMatchingSelected(false)
    setDeselectedMatching(new Set())
    lastSelectedIndex.current = null
    setActionError(null)
    setMessage(null)
    setPage(1)
  }

  const applyFilters = async () => {
    const nextSource = isBlurSort(draftSort)
      ? UltraDAIZeroCropSource.MLULTRA
      : draftSource
    setModelVersion(draftModelVersion)
    setSort(draftSort)
    setSource(nextSource)
    setStatuses(draftStatuses)
    setVerificationSources(draftVerificationSources)
    setMembership(draftMembership)
    setCreatedAfter(draftCreatedAfter)
    setCreatedBefore(draftCreatedBefore)
    if (searchUpc) {
      await loadCropsPage(
        1,
        searchUpc,
        draftModelVersion,
        draftSort,
        nextSource,
        draftStatuses,
        draftMembership,
        draftCreatedAfter,
        draftCreatedBefore,
        draftVerificationSources,
      )
    }
  }

  const refresh = async () => {
    setSelected(new Set())
    setAllMatchingSelected(false)
    setDeselectedMatching(new Set())
    setMessage(
      t(
        'daiZeroReview.saved.pendingUpdate',
        'Saved. Changes are pending the next DAI Zero update.',
      ),
    )
    try {
      await refetch?.()
    } catch {
      // Query errors render separately; the mutation has already committed.
    }
  }

  const currentFilterInput = (): UltraDAIZeroReviewInput => ({
    upc: searchUpc,
    modelVersion,
    sort,
    source: source || null,
    statuses,
    verificationSources:
      verificationSources.length === ALL_VERIFICATION_SOURCES.length
        ? null
        : verificationSources,
    membership,
    createdAfter: createdAfter
      ? new Date(`${createdAfter}T00:00:00`).toISOString()
      : null,
    createdBefore: createdBefore
      ? new Date(`${createdBefore}T23:59:59.999`).toISOString()
      : null,
  })

  const mutationSelection = allMatchingSelected
    ? {
        allMatching: currentFilterInput(),
        excludedCrops: Array.from(deselectedMatching).map(key => {
          const [cropSource, ...cropId] = key.split(':')
          return {
            source: cropSource as UltraDAIZeroCropSource,
            cropId: cropId.join(':'),
          }
        }),
      }
    : { crops: cropInput }

  const handleMutationResult = async (
    success: boolean | undefined,
    queued: boolean | undefined,
    submittedCount: number,
  ) => {
    if (success === false) {
      throw new Error(
        t('daiZeroReview.error.actionFailed', 'The review action failed.'),
      )
    }
    if (queued) {
      setSelected(new Set())
      setAllMatchingSelected(false)
      setDeselectedMatching(new Set())
      setMessage(
        t('daiZeroReview.saved.queued', {
          count: submittedCount,
          defaultValue: `Submitted ${submittedCount.toLocaleString()} crops for processing`,
        }),
      )
      return
    }
    await refresh()
  }

  const submitReviewAction = async (
    runMutation: () => Promise<
      | {
          success: boolean
          queued: boolean
        }
      | null
      | undefined
    >,
    onSuccess?: () => void,
  ) => {
    if (!connection || !selectedCount) return false
    setActionError(null)
    setMessage(null)
    setActionProgress(
      t('daiZeroReview.action.submitting', {
        count: selectedCount,
        defaultValue: `Submitting changes for ${selectedCount.toLocaleString()} crops…`,
      }),
    )
    try {
      const result = await runMutation()
      await handleMutationResult(result?.success, result?.queued, selectedCount)
      onSuccess?.()
      return true
    } catch (caught) {
      reportActionError(caught)
      return false
    } finally {
      setActionProgress(null)
    }
  }

  const submitExclusion = async (excluded: boolean) => {
    if (!connection) return
    await submitReviewAction(async () => {
      const result = await setExclusion({
        variables: {
          input: {
            modelVersion: connection.modelVersion,
            ...mutationSelection,
            excluded,
          },
        },
      })
      return result.data?.setDaiZeroCropExclusion
    })
  }

  const submitRelabel = async () => {
    if (!connection || !selectedCount || !replacementProduct) return
    await submitReviewAction(
      async () => {
        const result = await relabel({
          variables: {
            input: {
              modelVersion: connection.modelVersion,
              ...mutationSelection,
              replacementUpc: replacementProduct.upcA,
            },
          },
        })
        return result.data?.relabelDaiZeroCrops
      },
      () => {
        setRelabelOpen(false)
        setReplacementProduct(null)
      },
    )
  }

  const submitUnverify = async () => {
    if (!connection || !selectedCount) return
    await submitReviewAction(async () => {
      const result = await unverify({
        variables: {
          input: {
            modelVersion: connection.modelVersion,
            ...mutationSelection,
          },
        },
      })
      return result.data?.unverifyDaiZeroCrops
    })
  }

  const submitUndoHistoricalRelabel = async () => {
    if (!connection || !selectedCount) return
    await submitReviewAction(async () => {
      const result = await undoHistoricalRelabel({
        variables: {
          input: {
            modelVersion: connection.modelVersion,
            ...mutationSelection,
          },
        },
      })
      return result.data?.undoHistoricalDaiZeroRelabels
    })
  }

  return (
    <>
      <Helmet title={t('daiZeroReview.title', 'Labeled Crop Review')} />
      <Stack
        spacing={2}
        sx={{
          p: 3,
          height: 'calc(100dvh - 104px)',
          minHeight: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={2} sx={{ flexShrink: 0 }}>
          <Typography variant="h4">
            {t('daiZeroReview.title', 'Labeled Crop Review')}
          </Typography>
          <Alert severity="info">
            {t(
              'daiZeroReview.notice.nextUpdate',
              'Review actions affect the next successful incremental update, not the currently deployed model.',
            )}
          </Alert>
          {modelsError && (
            <Alert severity="error">
              {t('daiZeroReview.model.loadFailed', {
                message: modelsError.message,
                defaultValue: `Unable to load DAI Zero models: ${modelsError.message}`,
              })}
            </Alert>
          )}
          {selectedModel && !selectedModel.manifestAvailable && (
            <Alert severity="warning">
              {t('daiZeroReview.model.manifestUnavailable', {
                version: selectedModel.version,
                defaultValue: `DAI Zero v${selectedModel.version} does not have a review manifest. Select a newer model.`,
              })}
            </Alert>
          )}
          {filterSidebarElement &&
            createPortal(
              <ProductSearchAutocomplete
                value={searchProduct}
                label={t('daiZeroReview.search.label', 'Search product')}
                placeholder={t(
                  'daiZeroReview.search.placeholder',
                  'Search by product name, brand, or UPC',
                )}
                minWidth={0}
                fullWidth
                size="small"
                disabled={!selectedModel?.manifestAvailable || modelsLoading}
                onChange={product => {
                  setSearchProduct(product)
                  if (!product) return
                  const nextUpc = product.upcA
                  resetSearchState()
                  setSearchUpc(nextUpc)
                  void loadCropsPage(1, nextUpc, modelVersion)
                }}
              />,
              filterSidebarElement,
            )}
          {error && (
            <Alert severity="error">
              {t('daiZeroReview.error.loadFailed', {
                message: error.message,
                defaultValue: `Unable to load crops: ${error.message}`,
              })}
            </Alert>
          )}
          {actionError && <Alert severity="error">{actionError}</Alert>}
          <>
            {connection && (
              <>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Chip
                    label={t('daiZeroReview.selectedModelVersion', {
                      version: connection.modelVersion,
                      defaultValue: `v${connection.modelVersion}`,
                    })}
                    color="primary"
                  />
                  {connection.modelDeployed && (
                    <Chip
                      label={t('daiZeroReview.model.deployed', 'Deployed')}
                      color="success"
                    />
                  )}
                  <Typography>
                    {t('daiZeroReview.cropCount', {
                      count: connection.totalCount,
                      upc: searchUpc,
                      defaultValue: `${connection.totalCount.toLocaleString()} crops for UPC ${searchUpc}`,
                    })}
                  </Typography>
                  <ProductImageBox
                    imageUrl={
                      searchProduct?.upcA === searchUpc
                        ? searchProduct.marketingImageUrl
                        : null
                    }
                    size={56}
                    noImageLabel={t(
                      'daiZeroReview.productImage.unavailable',
                      'No image',
                    )}
                    alt={t('daiZeroReview.productImage.alt', {
                      upc: searchUpc,
                      defaultValue: `Marketing image for UPC ${searchUpc}`,
                    })}
                  />
                  <Typography color="text.secondary">
                    {t('daiZeroReview.eligibleCount', {
                      manifestCount: connection.manifestCount,
                      eligibleCount: connection.eligibleCount,
                      defaultValue: `${connection.manifestCount.toLocaleString()} in DAI Zero · ${connection.eligibleCount.toLocaleString()} eligible`,
                    })}
                  </Typography>
                  <ProductModelExclusionControl
                    key={searchUpc}
                    upc={searchUpc}
                  />
                </Stack>
                <Stack spacing={0.5} alignItems="flex-start">
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 96, flexShrink: 0 }}
                    >
                      {t('daiZeroReview.selection.label', 'Selection')}
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Button
                        disabled={
                          !crops.length ||
                          (allMatchingSelected
                            ? deselectedMatching.size === 0
                            : crops.every(crop =>
                                selected.has(`${crop.source}:${crop.cropId}`),
                              ))
                        }
                        onClick={() => {
                          if (allMatchingSelected) {
                            setDeselectedMatching(current => {
                              const next = new Set(current)
                              crops.forEach(crop =>
                                next.delete(`${crop.source}:${crop.cropId}`),
                              )
                              return next
                            })
                            return
                          }
                          setSelected(
                            new Set(
                              crops.map(
                                crop => `${crop.source}:${crop.cropId}`,
                              ),
                            ),
                          )
                        }}
                      >
                        {t(
                          'daiZeroReview.selection.selectVisible',
                          'Select visible',
                        )}
                      </Button>
                      <Button
                        disabled={!connection.totalCount || allMatchingSelected}
                        onClick={() => {
                          setAllMatchingSelected(true)
                          setSelected(new Set())
                          setDeselectedMatching(new Set())
                        }}
                      >
                        {t('daiZeroReview.selection.selectAllMatching', {
                          count: connection.totalCount,
                          defaultValue: `Select all ${connection.totalCount.toLocaleString()} matching crops`,
                        })}
                      </Button>
                      <Button
                        disabled={!selectedCount}
                        onClick={() => {
                          setSelected(new Set())
                          setAllMatchingSelected(false)
                          setDeselectedMatching(new Set())
                        }}
                      >
                        {t(
                          'daiZeroReview.selection.deselectAll',
                          'Deselect all',
                        )}
                      </Button>
                    </Stack>
                    {allMatchingSelected && (
                      <Typography variant="caption" color="text.secondary">
                        {t('daiZeroReview.selection.allMatchingSelected', {
                          count: selectedCount,
                          defaultValue: `All ${selectedCount.toLocaleString()} matching crops are selected. Deselecting a crop removes only that crop from the bulk action.`,
                        })}
                      </Typography>
                    )}
                    {allMatchingSelected && requiresAsyncProcessing && (
                      <Typography variant="caption" color="text.secondary">
                        {t(
                          'daiZeroReview.selection.largeSelectionNotice',
                          'Selections over 500 crops process in the background.',
                        )}
                      </Typography>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ width: 96, flexShrink: 0 }}
                    >
                      {t('daiZeroReview.actions.label', 'Review actions')}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <Button
                        disabled={!canRelabel || actionLoading}
                        onClick={() => setRelabelOpen(true)}
                      >
                        {t('daiZeroReview.actions.relabel', 'Relabel')}
                      </Button>
                      <Button
                        disabled={!canUnverify || actionLoading}
                        onClick={() => void submitUnverify()}
                      >
                        {t('daiZeroReview.actions.unverify', 'Unverify')}
                      </Button>
                      <Button
                        disabled={!canExclude || actionLoading}
                        onClick={() => setConfirmExclusion(true)}
                      >
                        {t('daiZeroReview.actions.exclude', 'Exclude')}
                      </Button>
                      <Button
                        disabled={!canCancelExclusion || actionLoading}
                        onClick={() => setConfirmExclusion(false)}
                      >
                        {t(
                          'daiZeroReview.actions.cancelExclusion',
                          'Cancel exclusion',
                        )}
                      </Button>
                      <Button
                        disabled={!canUndoHistoricalRelabel || actionLoading}
                        onClick={() => void submitUndoHistoricalRelabel()}
                      >
                        {t(
                          'daiZeroReview.actions.undoHistoricalRelabel',
                          'Undo historical relabel',
                        )}
                      </Button>
                    </Stack>
                    {selectedCount > 0 && !hasAvailableAction && (
                      <Alert severity="info" sx={{ py: 0.25 }}>
                        {t(
                          'daiZeroReview.actions.incompatibleSelection',
                          'The selected crops do not share a compatible review status or source. Narrow the Status or Source filters and apply them, or adjust the current selection.',
                        )}
                      </Alert>
                    )}
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography>
                    {t('daiZeroReview.cropSize', 'Crop size')}
                  </Typography>
                  <Slider
                    aria-label={t('daiZeroReview.cropSize', 'Crop size')}
                    min={120}
                    max={300}
                    step={20}
                    value={tileSize}
                    onChange={(_, value) =>
                      setTileSize(Array.isArray(value) ? value[0] : value)
                    }
                    sx={{ maxWidth: 240 }}
                  />
                </Stack>
              </>
            )}
            {filterSidebarElement &&
              createPortal(
                <Stack
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, minmax(0, 1fr))',
                      lg: '1fr',
                    },
                    gap: 1.5,
                  }}
                >
                  {searchUpc && !searchProduct && (
                    <Typography variant="caption" color="text.secondary">
                      {t('daiZeroReview.search.currentUpc', {
                        upc: searchUpc,
                        defaultValue: `UPC ${searchUpc}`,
                      })}
                    </Typography>
                  )}
                  <FormControl size="small" disabled={modelsLoading}>
                    <InputLabel id="dai-zero-review-model-label">
                      {t('daiZeroReview.model.label', 'DAI Zero model')}
                    </InputLabel>
                    <Select
                      labelId="dai-zero-review-model-label"
                      label={t('daiZeroReview.model.label', 'DAI Zero model')}
                      value={draftModelVersion}
                      onChange={event => {
                        const nextVersion = event.target.value
                        setDraftModelVersion(nextVersion)
                      }}
                    >
                      {reviewModels.map(model => (
                        <MenuItem
                          key={model.version}
                          value={model.version}
                          disabled={!model.manifestAvailable}
                        >
                          {t('daiZeroReview.model.versionOption', {
                            version: model.version,
                            status: model.deployed
                              ? t('daiZeroReview.model.deployed', 'Deployed')
                              : t(
                                  'daiZeroReview.model.notDeployed',
                                  'Not deployed',
                                ),
                            manifest: model.manifestAvailable
                              ? ''
                              : ` · ${t(
                                  'daiZeroReview.model.noManifest',
                                  'No manifest',
                                )}`,
                            defaultValue: `v${model.version} · {status}{manifest}`,
                          })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 190 }}>
                    <InputLabel id="dai-zero-review-sort-label">
                      {t('daiZeroReview.sort.label', 'Sort by')}
                    </InputLabel>
                    <Select
                      labelId="dai-zero-review-sort-label"
                      label={t('daiZeroReview.sort.label', 'Sort by')}
                      value={draftSort}
                      onChange={event => {
                        const nextSort = event.target
                          .value as UltraDAIZeroReviewSort
                        setDraftSort(nextSort)
                        if (isBlurSort(nextSort)) {
                          setDraftSource(UltraDAIZeroCropSource.MLULTRA)
                        }
                      }}
                    >
                      <MenuItem value={UltraDAIZeroReviewSort.CREATED_DESC}>
                        {t('daiZeroReview.sort.createdNewest', 'Newest first')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewSort.CREATED_ASC}>
                        {t('daiZeroReview.sort.createdOldest', 'Oldest first')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewSort.FOCUS_ASC}>
                        {t(
                          'daiZeroReview.sort.blurriestFirst',
                          'Blurriest first',
                        )}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewSort.FOCUS_DESC}>
                        {t(
                          'daiZeroReview.sort.sharpestFirst',
                          'Sharpest first',
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  {isBlurSort(draftSort) && (
                    <Alert severity="info" sx={{ py: 0 }}>
                      {t(
                        'daiZeroReview.sort.blurSourceNotice',
                        'Blur scores are only available for MLUltra crops, so historical crops are hidden.',
                      )}
                    </Alert>
                  )}
                  <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel id="dai-zero-review-source-label">
                      {t('daiZeroReview.sourceFilter.label', 'Source')}
                    </InputLabel>
                    <Select
                      labelId="dai-zero-review-source-label"
                      label={t('daiZeroReview.sourceFilter.label', 'Source')}
                      value={draftSource}
                      displayEmpty
                      renderValue={value =>
                        value ||
                        t('daiZeroReview.sourceFilter.all', 'All sources')
                      }
                      disabled={isBlurSort(draftSort)}
                      onChange={event => {
                        const nextSource = event.target.value as
                          | UltraDAIZeroCropSource
                          | ''
                        setDraftSource(nextSource)
                      }}
                    >
                      <MenuItem value="">
                        {t('daiZeroReview.sourceFilter.all', 'All sources')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroCropSource.MLULTRA}>
                        {t('daiZeroReview.cropCard.source.mlUltra', 'MLUltra')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroCropSource.HISTORICAL}>
                        {t(
                          'daiZeroReview.cropCard.source.historical',
                          'Historical',
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="dai-zero-review-verification-source-label">
                      {t(
                        'daiZeroReview.verificationSourceFilter.label',
                        'Verification source',
                      )}
                    </InputLabel>
                    <Select
                      multiple
                      labelId="dai-zero-review-verification-source-label"
                      label={t(
                        'daiZeroReview.verificationSourceFilter.label',
                        'Verification source',
                      )}
                      value={draftVerificationSources}
                      renderValue={selected =>
                        selected.length === ALL_VERIFICATION_SOURCES.length
                          ? t(
                              'daiZeroReview.verificationSourceFilter.all',
                              'All verification sources',
                            )
                          : t('daiZeroReview.statusFilter.selectedCount', {
                              count: selected.length,
                              defaultValue: `${selected.length} selected`,
                            })
                      }
                      onChange={event => {
                        const nextVerificationSources = event.target
                          .value as UltraDAIZeroVerificationSource[]
                        setDraftVerificationSources(nextVerificationSources)
                      }}
                    >
                      <MenuItem
                        value={UltraDAIZeroVerificationSource.BARCODE_SCAN}
                      >
                        {t(
                          'daiZeroReview.verificationSource.barcodeScan',
                          'Barcode scan',
                        )}
                      </MenuItem>
                      <MenuItem
                        value={UltraDAIZeroVerificationSource.WHITE_RABBIT}
                      >
                        {t(
                          'daiZeroReview.verificationSource.whiteRabbit',
                          'White Rabbit',
                        )}
                      </MenuItem>
                      <MenuItem
                        value={UltraDAIZeroVerificationSource.HISTORICAL}
                      >
                        {t(
                          'daiZeroReview.verificationSource.historical',
                          'Historical label',
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="dai-zero-review-status-label">
                      {t('daiZeroReview.statusFilter.label', 'Status')}
                    </InputLabel>
                    <Select
                      multiple
                      labelId="dai-zero-review-status-label"
                      label={t('daiZeroReview.statusFilter.label', 'Status')}
                      value={draftStatuses}
                      renderValue={selected =>
                        selected.length === ALL_STATUSES.length
                          ? t('daiZeroReview.statusFilter.all', 'All statuses')
                          : t('daiZeroReview.statusFilter.selectedCount', {
                              count: selected.length,
                              defaultValue: `${selected.length} selected`,
                            })
                      }
                      onChange={event => {
                        const nextStatuses = event.target
                          .value as UltraDAIZeroReviewStatus[]
                        setDraftStatuses(nextStatuses)
                      }}
                    >
                      <MenuItem value={UltraDAIZeroReviewStatus.UNCHANGED}>
                        {t('daiZeroReview.statusFilter.unchanged', 'Unchanged')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewStatus.EXCLUDED}>
                        {t('daiZeroReview.statusFilter.excluded', 'Excluded')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewStatus.RELABELED}>
                        {t('daiZeroReview.statusFilter.relabeled', 'Relabeled')}
                      </MenuItem>
                      <MenuItem value={UltraDAIZeroReviewStatus.UNVERIFIED}>
                        {t(
                          'daiZeroReview.statusFilter.unverified',
                          'Unverified',
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel id="dai-zero-review-membership-label">
                      {t(
                        'daiZeroReview.membershipFilter.label',
                        'Dataset membership',
                      )}
                    </InputLabel>
                    <Select
                      labelId="dai-zero-review-membership-label"
                      label={t(
                        'daiZeroReview.membershipFilter.label',
                        'Dataset membership',
                      )}
                      value={draftMembership}
                      onChange={event => {
                        const next = event.target
                          .value as UltraDAIZeroReviewMembership
                        setDraftMembership(next)
                      }}
                    >
                      <MenuItem
                        value={UltraDAIZeroReviewMembership.IN_DAI_ZERO}
                      >
                        {t(
                          'daiZeroReview.membershipFilter.inDaiZero',
                          'In DAI Zero',
                        )}
                      </MenuItem>
                      <MenuItem
                        value={UltraDAIZeroReviewMembership.ALL_ELIGIBLE}
                      >
                        {t(
                          'daiZeroReview.membershipFilter.allEligible',
                          'All eligible',
                        )}
                      </MenuItem>
                      <MenuItem
                        value={
                          UltraDAIZeroReviewMembership.ELIGIBLE_NOT_IN_DAI_ZERO
                        }
                      >
                        {t(
                          'daiZeroReview.membershipFilter.reserve',
                          'Eligible, not in DAI Zero',
                        )}
                      </MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="date"
                    label={t('daiZeroReview.dateFilter.after', 'Created after')}
                    value={draftCreatedAfter}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={event => {
                      setDraftCreatedAfter(event.target.value)
                    }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label={t(
                      'daiZeroReview.dateFilter.before',
                      'Created before',
                    )}
                    value={draftCreatedBefore}
                    slotProps={{ inputLabel: { shrink: true } }}
                    onChange={event => {
                      setDraftCreatedBefore(event.target.value)
                    }}
                  />
                  <Button
                    variant="contained"
                    disabled={!searchUpc || loading}
                    onClick={() => void applyFilters()}
                  >
                    {t('daiZeroReview.filters.apply', 'Apply filters')}
                  </Button>
                </Stack>,
                filterSidebarElement,
              )}
          </>
        </Stack>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: 2,
            alignItems: 'stretch',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Paper
            ref={setFilterSidebarElement}
            sx={{
              p: 1.5,
              borderRadius: 1,
              width: { xs: '100%', lg: 280 },
              flexShrink: 0,
              alignSelf: { lg: 'flex-start' },
              maxHeight: { lg: '100%' },
              overflow: { lg: 'auto' },
            }}
          />
          {(connection || loading) && (
            <Stack
              spacing={2}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: 0,
                overflow: 'hidden',
              }}
            >
              <Box
                ref={cropGridRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  display: 'flex',
                  gap: 1,
                  flexWrap: 'wrap',
                  alignContent: loading ? 'center' : 'flex-start',
                  justifyContent: loading ? 'center' : 'flex-start',
                }}
              >
                {loading ? (
                  <CircularProgress
                    aria-label={t('common.loading', 'Loading')}
                  />
                ) : (
                  crops.map((crop, cropIndex) => {
                    const key = `${crop.source}:${crop.cropId}`
                    return (
                      <DAIZeroReviewCropCard
                        key={key}
                        crop={crop}
                        size={tileSize}
                        selected={
                          allMatchingSelected
                            ? !deselectedMatching.has(key)
                            : selected.has(key)
                        }
                        onSelect={shiftKey => {
                          if (allMatchingSelected) {
                            setDeselectedMatching(current => {
                              const next = new Set(current)
                              if (next.has(key)) next.delete(key)
                              else next.add(key)
                              return next
                            })
                            return
                          }
                          setSelected(current => {
                            const next = new Set(current)
                            if (
                              shiftKey &&
                              lastSelectedIndex.current !== null
                            ) {
                              const start = Math.min(
                                lastSelectedIndex.current,
                                cropIndex,
                              )
                              const end = Math.max(
                                lastSelectedIndex.current,
                                cropIndex,
                              )
                              crops
                                .slice(start, end + 1)
                                .forEach(item =>
                                  next.add(`${item.source}:${item.cropId}`),
                                )
                            } else if (next.has(key)) {
                              next.delete(key)
                            } else {
                              next.add(key)
                            }
                            lastSelectedIndex.current = cropIndex
                            return next
                          })
                        }}
                        onOpen={() =>
                          setFullImageCropId(`${crop.source}:${crop.cropId}`)
                        }
                      />
                    )
                  })
                )}
              </Box>
              {connection && totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page}
                  disabled={loading}
                  onChange={(_, nextPage) => {
                    if (nextPage === page || !searchUpc) return
                    void loadCropsPage(
                      nextPage,
                      searchUpc,
                      modelVersion,
                      sort,
                      source,
                      statuses,
                      membership,
                      createdAfter,
                      createdBefore,
                      verificationSources,
                      true,
                    )
                  }}
                  showFirstButton
                  showLastButton
                  color="primary"
                  shape="rounded"
                  sx={{ flexShrink: 0 }}
                />
              )}
            </Stack>
          )}
        </Box>
      </Stack>
      <Dialog
        open={relabelOpen}
        onClose={() => {
          setRelabelOpen(false)
          setReplacementProduct(null)
        }}
      >
        <DialogTitle>
          {t('daiZeroReview.relabel.title', {
            count: selectedCount,
            defaultValue: `Relabel ${selectedCount} crops`,
          })}
        </DialogTitle>
        <DialogContent>
          <ProductSearchAutocomplete
            value={replacementProduct}
            label={t(
              'daiZeroReview.relabel.replacementProduct',
              'Replacement product',
            )}
            placeholder={t(
              'daiZeroReview.search.placeholder',
              'Search by product name, brand, or UPC',
            )}
            onChange={setReplacementProduct}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRelabelOpen(false)
              setReplacementProduct(null)
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!replacementProduct || relabeling}
            onClick={() => void submitRelabel()}
          >
            {t('daiZeroReview.relabel.confirm', 'Confirm relabel')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmExclusion !== null}
        onClose={() => setConfirmExclusion(null)}
      >
        <DialogTitle>
          {confirmExclusion
            ? t(
                'daiZeroReview.exclusion.confirmTitle',
                'Exclude selected crops?',
              )
            : t(
                'daiZeroReview.exclusion.cancelTitle',
                'Cancel pending exclusions?',
              )}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmExclusion
              ? t(
                  'daiZeroReview.exclusion.confirmDescription',
                  'The bounding boxes remain valid, but their embeddings will be forgotten during the next DAI Zero incremental update.',
                )
              : t(
                  'daiZeroReview.exclusion.cancelDescription',
                  'These crops will remain eligible for the next DAI Zero update.',
                )}
          </Typography>
          {requiresAsyncProcessing && (
            <Typography sx={{ mt: 1 }} color="text.secondary">
              {t(
                'daiZeroReview.exclusion.asyncDescription',
                'Because this selection is large, the update will process in the background. Refresh after a few minutes to see all changes.',
              )}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmExclusion(null)}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            variant="contained"
            color={confirmExclusion ? 'warning' : 'primary'}
            disabled={excluding}
            onClick={async () => {
              const value = Boolean(confirmExclusion)
              setConfirmExclusion(null)
              await submitExclusion(value)
            }}
          >
            {t('common.confirm', 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(actionProgress || message)}
        autoHideDuration={actionProgress ? null : 4000}
        onClose={(_, reason) => {
          if (reason !== 'clickaway' && !actionProgress) setMessage(null)
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={actionProgress ? 'info' : 'success'}
          onClose={actionProgress ? undefined : () => setMessage(null)}
        >
          {actionProgress ?? message}
        </Alert>
      </Snackbar>
      <FullImageDialog
        box={fullImageCrop}
        open={Boolean(fullImageCrop)}
        onClose={() => setFullImageCropId(null)}
        selectedUpc={fullImageCrop?.upc}
        showSelectedProduct={false}
        fetchPreviewContext={fullImageCrop?.source === 'MLULTRA'}
      />
    </>
  )
}

export default DAIZeroDatasetReview
